import showToast from '@Common/toast/showToast';
import { CognitoAccessToken } from '@Models/types';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { jwtDecode } from 'jwt-decode';

const EXCLUDED_PATHS = ['/api/auth/callback'];

export const createAxiosInstance = (
  logout: () => void,
  baseURL?: string
): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    // withCredentials: true,
  });

  const isTokenExpired = (token: string) => {
    if (token) {
      try {
        const accessTokenParsed = jwtDecode(token) as CognitoAccessToken;
        const now = Math.floor(Date.now() / 1000);
        return accessTokenParsed.exp < now;
      } catch (err) {
        console.warn('Failed to decode token:', err);
        return true;
      }
    }
    return false;
  };

  instance.interceptors.request.use(async (config) => {
    const url = config.url || '';
    const isExcluded = EXCLUDED_PATHS.some((path) => url?.includes(path));
    if (isExcluded) {
      // Exclude credentials and Authorization header
      config.withCredentials = false;
      delete config.headers.Authorization;
    } else {
      const token = localStorage.getItem('accessToken');
      if (!token || isTokenExpired(token)) {
        try {
          const session = await fetchAuthSession({ forceRefresh: true });
          const newToken = session.tokens?.accessToken?.toString() as string;
          if (newToken) {
            localStorage.setItem('accessToken', newToken);
          }
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (err) {
          logout();
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status;

      if (status === 401) {
        console.warn('401 detected. Logging out...');
        showToast.error('Session expired. Login again', 'session-expiry');
        logout();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};
